import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { DigitalSolutionsService } from '../../services/digital-solutions.service';
import { MetasService } from '../../services/metas.service';
import { SchemasService } from '../../services/schemas.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { Solution } from '../models/solutions.model';
import { CommonModule, isPlatformBrowser, NgOptimizedImage } from '@angular/common';
import { Metas } from '../../services/models/metas.model';
import { SafehtmlPipe } from '../../pipes/safehtml.pipe';
import { CardSolutionComponent } from '../components/card-solution/card-solution.component';
import { BootstrapLoaderService } from '../../services/bootstrap-loader.service';

@Component({
  selector: 'app-detail',
  imports: [CommonModule, NgOptimizedImage, RouterModule, SafehtmlPipe, CardSolutionComponent],
  templateUrl: './detail.component.html',
  styleUrl: './detail.component.scss',
})
export class DetailComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private solutionService = inject(DigitalSolutionsService);
  private metasService = inject(MetasService);
  private schemasService = inject(SchemasService);
  private platformId = inject(PLATFORM_ID);
  private bootstrapLoader = inject(BootstrapLoaderService);
  private destroyRef = inject(DestroyRef);

  /* ----------------------------- Signals ----------------------------- */

  private paramMap = toSignal(this.route.paramMap);
  private slug = computed(() => this.paramMap()?.get('solution'));

  solution = signal<Solution>({
    id: 0,
    robots: '',
    metaTitle: '',
    metaDescription: '',
    ogImage: '',
    dublinSubject: '',
    schema: '',
    name: '',
    slug: '',
    isActive: true,
    shortDescription: '',
    iosLink: '',
    androidLink: '',
    content: '',
    bannerWeb: {
      id: 0,
      name: '',
      alt: '',
      order: 0,
      image: '',
    },
    bannerMobile: {
      id: 0,
      name: '',
      alt: '',
      order: 0,
      image: '',
    },
    listImage: {
      id: 0,
      name: '',
      alt: '',
      order: 0,
      image: '',
    },
  });
  solutions = signal<Solution[]>([]);
  private windowWidth = signal(isPlatformBrowser(this.platformId) ? window.innerWidth : 0);

  backgroundImageUrl = computed(() => {
    const solution = this.solution();
    if (!solution || !isPlatformBrowser(this.platformId)) return '';

    return this.windowWidth() > 992 ? solution.bannerWeb.image : solution.bannerMobile.image;
  });

  /* ----------------------------- Init ----------------------------- */

  constructor() {
    this.bootstrapLoader.load();
    this.listenResize();
    effect(() => {
      const slug = this.slug();
      if (!slug) return;
      this.loadSolution(slug);
    });
  }

  /* ----------------------------- Logic ----------------------------- */

  private loadSolution(slug: string): void {
    this.solutionService.getSolutionBySlug(slug).subscribe({
      next: (solution) => {
        this.solution.set(solution);
        this.setMetas(solution);
        this.loadRelatedSolutions(solution.id);
      },
      error: (err) => {
        console.error(err);
        if (err.status === 404) {
          this.router.navigate(['404']);
        }
      },
    });
  }

  private loadRelatedSolutions(currentId: number): void {
    this.solutionService.getSolutions().subscribe({
      next: (res) => {
        if (res?.count > 0) {
          this.solutions.set(res.results.filter((s) => s.id !== currentId));
        }
      },
      error: (err) => console.error(err),
    });
  }

  /* ----------------------------- SEO ----------------------------- */

  private setMetas(solution: Solution): void {
    const metas: Metas = {
      description: solution.metaDescription,
      image: solution.ogImage ?? '',
      robots: 'index,follow',
      subject: solution.dublinSubject,
      title: solution.metaTitle,
      schema: solution.schema ? this.schemasService.parseJsonSafely(solution.schema) : {},
    };

    this.metasService.addMetas(metas);

    if (!isPlatformBrowser(this.platformId) && metas.schema) {
      this.schemasService.addLdJsonScript(metas.schema);
    }
  }

  /* ----------------------------- Resize ----------------------------- */

  private listenResize(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const onResize = () => this.windowWidth.set(window.innerWidth);
    window.addEventListener('resize', onResize);
    this.destroyRef.onDestroy(() => window.removeEventListener('resize', onResize));
  }
}
